import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Button } from './button';
import { Dialog, DialogTitle, DialogBody } from './dialog';
import SearchableListbox from './SearchableListbox';
import {
  DocumentTextIcon, ShieldExclamationIcon, PencilSquareIcon,
  InboxArrowDownIcon, EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { useUser } from '../components/UserContext';
import { useClients, useContracts, useConvenios, useAfianzadoras } from '../contexts/DataContext';
import { useOrgMiembros } from '../hooks/useOrgMiembros';
import AsignadosBubbles from './AsignadoBubble';
import { logBitacora } from '../utils/bitacora';
import { opcionesAfianzadora, mapAfianzadoraName } from '../utils/afianzadoras';
import {
  PENDIENTE_TIPOS,
  PENDIENTE_TIPO_LABELS,
  TARGET_TIPOS,
  TITULO_MAX_LEN,
  DETALLE_MAX_LEN,
  SUGERENCIAS_POR_TIPO,
  ORG_LABEL,
  TIPOS_CREABLES,
  buildPendiente,
  defaultsParaTipo,
  esDeLaOrg,
  requiereAfianzadora,
  validarPendiente,
  nivelesParaTipo,
  resolverTarget,
  normalizarAsignados,
  buildActividad,
} from '../utils/pendientes';

const msToDateInput = (ms) => {
  if (!ms) return '';
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const dateInputToMs = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  const t = new Date(y, m - 1, d).getTime();
  return Number.isNaN(t) ? null : t;
};

const field =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

// Tipo cards, in the order an agente meets them. `compromiso_afianzadora` leads
// because it is the one with a deadline attached to a live fianza. Only the
// creatable tipos: an endoso is requested from the fianza's own historial.
const TIPO_ICONS = {
  [PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA]: ShieldExclamationIcon,
  [PENDIENTE_TIPOS.DOCUMENTO]: DocumentTextIcon,
  [PENDIENTE_TIPOS.ACTA_ENTREGA]: InboxArrowDownIcon,
  [PENDIENTE_TIPOS.CONVENIO_MODIFICATORIO]: PencilSquareIcon,
  [PENDIENTE_TIPOS.OTRO]: EllipsisHorizontalIcon,
};
const TIPO_CARDS = TIPOS_CREABLES.map((tipo) => ({ tipo, Icon: TIPO_ICONS[tipo] }));

// A locked relation renders as a read-only line instead of a picker. Built
// from whatever the calling surface (or the stored doc, on edit) already knows.
function resumenTarget(src, names) {
  if (!src) return null;
  if (esDeLaOrg(src)) return `De la organización`;
  const partes = [];
  if (src.convenioId) partes.push(names.convenio || 'Convenio');
  else if (src.clienteId) partes.push(names.cliente || 'Fiado');
  if (src.contratoTitulo) partes.push(src.contratoTitulo);
  if (src.numeroFianza) partes.push(`Fianza #${src.numeroFianza}`);
  return partes.filter(Boolean).join(' · ');
}

/**
 * Create or edit a pendiente.
 *
 * Tipo-first: what the thing IS determines which level it hangs from and
 * whether an afianzadora is involved — so the form asks that first and
 * derives the rest, instead of making the agente fill in the data model.
 * Pendientes are internal to the team; nothing here is shown to the fiado.
 *
 * `preset` is what the calling surface already knows. From FianzaView it
 * carries clienteId + contratoId + fianzaId + the XML emisor, so a compromiso
 * is a title, a date and Guardar — prefilled, still editable. Only on EDIT is
 * the target fixed: moving a pendiente between fiados would retroactively
 * change who already saw it.
 */
export function PendienteDialog({
  isOpen, onClose, pendiente = null, preset = null, onSaved,
  // Which tipos this surface may create (null = every creatable tipo). A
  // contrato offers actas and convenios modificatorios, a fianza offers the
  // compromiso, the bolita offers whatever fits where the user is standing.
  tiposPermitidos = null,
}) {
  const { user, userData } = useUser();
  const { clientsList, clientsById } = useClients();
  const { contractsList } = useContracts();
  const { conveniosList, conveniosById } = useConvenios();
  const { afianzadorasList } = useAfianzadoras();
  const { miembros, nombreDe } = useOrgMiembros();

  const isEdit = Boolean(pendiente?.id);

  const [tipo, setTipo] = useState(null);
  const [target, setTarget] = useState('');     // "cliente:{id}" | "convenio:{id}"
  const [contratoId, setContratoId] = useState('');
  const [fianzaId, setFianzaId] = useState('');
  const [titulo, setTitulo] = useState('');
  const [detalle, setDetalle] = useState('');
  const [afianzadora, setAfianzadora] = useState('');
  const [fechaLimite, setFechaLimite] = useState('');
  const [asignados, setAsignados] = useState([]);
  // From an OT: which equipo is being asked (preset.equipos lists the org's).
  const [equipoId, setEquipoId] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const defaults = tipo ? defaultsParaTipo(tipo) : null;

  const cards = useMemo(
    () => TIPO_CARDS.filter((c) => !tiposPermitidos || tiposPermitidos.includes(c.tipo)),
    [tiposPermitidos]
  );

  // The form is filled ONCE per opening (and when the edited pendiente
  // changes identity). Callers build `preset`, `tipos` and the pendiente row
  // inline on every render, and DataContext re-renders them on every Firestore
  // snapshot, so depending on those objects reset a half-typed form. The
  // latest values are read through refs instead.
  const presetRef = useRef(preset);
  presetRef.current = preset;
  const pendienteRef = useRef(pendiente);
  pendienteRef.current = pendiente;
  const cardsRef = useRef(cards);
  cardsRef.current = cards;
  const afianzadorasRef = useRef(afianzadorasList);
  afianzadorasRef.current = afianzadorasList;
  const pendienteKey = pendiente?.id || null;

  useEffect(() => {
    if (!isOpen) return;
    const pendiente = pendienteRef.current;
    const cards = cardsRef.current;
    const src = pendiente || presetRef.current || {};
    // Editing or a preset that already names the tipo skips the tipo step; so
    // does a surface that only allows one tipo.
    const unico = cards.length === 1 ? cards[0].tipo : null;
    setTipo(src.tipo || unico || (isEdit ? PENDIENTE_TIPOS.OTRO : null));
    setTarget(
      src.convenioId ? `convenio:${src.convenioId}`
        : src.clienteId ? `cliente:${src.clienteId}` : ''
    );
    setContratoId(src.contratoId || '');
    setFianzaId(src.fianzaId || '');
    setEquipoId(src.equipoId || '');
    setTitulo(src.titulo || '');
    setDetalle(src.detalle || '');
    setAfianzadora(src.afianzadora || mapAfianzadoraNameSafe(src.afianzadoraRaw, afianzadorasRef.current));
    setFechaLimite(msToDateInput(src.fechaLimite));
    // Defaults to whoever is filing it; the picker lets them add or hand it
    // to teammates. On edit the stored list is the truth.
    setAsignados(pendiente ? normalizarAsignados(pendiente) : (user?.uid ? [user.uid] : []));
    setError(null);
  }, [isOpen, pendienteKey, isEdit, user?.uid]);

  // A preset PREFILLS the relation, it does not lock it: from ContratoView or
  // FianzaView the pendiente starts attached to that contrato/fianza and the
  // agente can still point it somewhere else (or nowhere). Only an existing
  // pendiente keeps its target, because moving it would change who already
  // saw it in their portal.
  const targetLocked = isEdit;

  // What the tipo allows decides which pickers appear and whether they are
  // optional. See NIVELES_POR_TIPO.
  const niveles = tipo ? nivelesParaTipo(tipo) : [];
  const permiteOrg = niveles.includes(TARGET_TIPOS.ORG);
  const permiteConvenio = niveles.includes(TARGET_TIPOS.CONVENIO);
  const permiteContrato = niveles.includes(TARGET_TIPOS.CONTRATO) || niveles.includes(TARGET_TIPOS.FIANZA);
  const permiteFianza = niveles.includes(TARGET_TIPOS.FIANZA);
  const soloContrato = niveles.length === 1 && niveles[0] === TARGET_TIPOS.CONTRATO;

  const targetOptions = useMemo(() => [
    ...clientsList
      .filter((c) => !c.archived)
      .map((c) => ({ value: `cliente:${c.id}`, label: c.nombreEmpresa || 'Sin nombre' })),
    ...(permiteConvenio ? conveniosList
      .map((c) => ({ value: `convenio:${c.id}`, label: `${c.nombre || 'Convenio'} (convenio)` })) : []),
  ].sort((a, b) => a.label.localeCompare(b.label, 'es')), [clientsList, conveniosList, permiteConvenio]);

  const [targetKind, targetId] = target ? target.split(':') : ['', ''];
  const clienteId = targetKind === 'cliente' ? targetId : '';
  const convenioId = targetKind === 'convenio' ? targetId : '';

  // Convenio contracts are identified by number: under one convenio every
  // contrato carries the same título (the convenio's), so the name tells the
  // agente nothing. A fiado's contratos keep the título, with the number as
  // a suffix when it exists.
  const contratoOptions = useMemo(() => {
    if (!clienteId && !convenioId) return [];
    return contractsList
      .filter((c) => (convenioId ? c.convenioId === convenioId : c.clientId === clienteId))
      .map((c) => {
        const numero = c.numeroContrato?.value || '';
        const titulo = c.tituloContrato?.value || '';
        const label = convenioId
          ? (numero || titulo || 'Contrato sin número')
          : `${titulo || numero || 'Contrato sin título'}${titulo && numero ? ` · ${numero}` : ''}`;
        return { value: c.id, label };
      });
  }, [contractsList, clienteId, convenioId]);

  const fianzaOptions = useMemo(() => {
    if (!contratoId) return [];
    const contrato = contractsList.find((c) => c.id === contratoId);
    return (contrato?.fianzas || []).map((f) => ({
      value: f.id,
      label: `#${f.numeroFianza || 'sin número'}${f.tipoFianza ? ` — ${f.tipoFianza}` : ''}`,
    }));
  }, [contractsList, contratoId]);

  const afianzadoraOptions = useMemo(
    () => opcionesAfianzadora(afianzadorasList),
    [afianzadorasList]
  );

  const sugerencias = tipo ? (SUGERENCIAS_POR_TIPO[tipo] || []) : [];

  // On edit the stored doc is the source; a preset only exists on create.
  const fuenteRelacion = pendiente || preset;
  const presetResumen = resumenTarget(fuenteRelacion, {
    cliente: clientsById[fuenteRelacion?.clienteId]?.nombreEmpresa,
    convenio: conveniosById[fuenteRelacion?.convenioId]?.nombre,
  });


  const resetAndClose = () => {
    setError(null);
    onClose();
  };

  // The tipo picks its level out of what is known; ids below that level stay
  // on the doc as origin context (a compromiso born in FianzaView keeps its
  // contratoId/fianzaId but targets the cliente).
  const resolveTarget = () => resolverTarget(tipo, { clienteId, contratoId, fianzaId, convenioId });

  const handleSubmit = async () => {
    const resolved = resolveTarget();
    const invalido = validarPendiente({
      tipo, titulo, afianzadora, targetTipo: resolved ? resolved.targetTipo : null,
    });
    if (invalido) {
      setError(invalido);
      return;
    }
    if (!userData?.org) {
      setError('Tu usuario no pertenece a ninguna organización.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const ts = Date.now();
      const deLaOrg = resolved.targetTipo === TARGET_TIPOS.ORG;
      const nombre = deLaOrg
        ? 'la organización'
        : convenioId
          ? (conveniosById[convenioId]?.nombre || 'Convenio')
          : (clientsById[clienteId]?.nombreEmpresa || 'Fiado');
      const canonica = afianzadora ? mapAfianzadoraName(afianzadora, afianzadorasList) : null;

      if (isEdit) {
        await updateDoc(doc(db, 'pendientes', pendiente.id), {
          tipo,
          titulo: titulo.trim().slice(0, TITULO_MAX_LEN),
          detalle: detalle.trim().slice(0, DETALLE_MAX_LEN) || null,
          afianzadora: canonica,
          fechaLimite: dateInputToMs(fechaLimite),
          asignados,
          asignadoA: asignados[0] || null,
          asignacionPor: user?.uid || null,
          updatedAt: ts,
          ...buildActividad('editado', { uid: user?.uid, nombre: userData?.nombre || userData?.correo || null }, null, ts),
        });
      } else {
        await addDoc(collection(db, 'pendientes'), buildPendiente({
          org: userData.org,
          ...resolved,
          clienteId: clienteId || null,
          convenioId: convenioId || null,
          contratoId: contratoId || null,
          fianzaId: fianzaId || null,
          tipo,
          titulo,
          detalle,
          afianzadora: canonica,
          afianzadoraRaw: preset?.afianzadoraRaw || null,
          fechaLimite: dateInputToMs(fechaLimite),
          asignados: asignados.length ? asignados : (user?.uid ? [user.uid] : []),
          createdBy: { uid: user?.uid || null, nombre: userData?.nombre || userData?.correo || null },
          otId: preset?.otId || null,
          equipoId: equipoId || null,
          ts,
        }));
      }

      try {
        const verbo = isEdit ? 'Editó' : 'Registró';
        const otros = asignados.filter((u) => u !== user?.uid).map((u) => nombreDe(u) || u);
        const aQuien = otros.length ? ` · asignado a ${otros.join(', ')}` : '';
        const desc = `${verbo} pendiente (${PENDIENTE_TIPO_LABELS[tipo] || tipo}) de ${nombre}: ${titulo.trim()}${aQuien}`;
        // `pendientes` is not in the rules' collection enum, so the entry
        // hangs off the related doc — or the org itself when there is none.
        await logBitacora({
          action: isEdit ? 'UPDATE' : 'CREATE',
          collection: deLaOrg ? 'orgs' : convenioId ? 'convenios' : 'clientes',
          documentId: deLaOrg ? userData.org : (convenioId || clienteId),
          user,
          userData,
          metadata: {
            description: desc,
            clientId: clienteId || null,
            convenioId: convenioId || null,
            contratoId: contratoId || null,
            pendienteTipo: tipo,
          },
        });
      } catch (e) {
        console.error('bitacora pendiente failed', e);
      }

      onSaved?.();
      resetAndClose();
    } catch (e) {
      console.error('guardar pendiente failed', e);
      setError(e.message || 'No se pudo guardar el pendiente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Step 1: what is it. Skipped on edit and whenever the caller named the tipo.
  if (isOpen && !tipo) {
    return (
      <Dialog open onClose={resetAndClose} size="lg">
        <DialogTitle><span className="text-azul-surelia">¿Qué falta?</span></DialogTitle>
        <DialogBody>
          {presetResumen && (
            <div className="mb-3 text-sm text-zinc-500">{presetResumen}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {cards.map(({ tipo: t, Icon }) => (
              <button
                key={t}
                onClick={() => setTipo(t)}
                className="flex items-center gap-3 rounded-lg border border-gray-300 px-3 py-3 text-left text-sm text-gray-800 hover:border-azul-surelia hover:bg-azul-surelia/5 transition-colors"
              >
                <Icon className="w-5 h-5 text-azul-surelia flex-shrink-0" />
                {PENDIENTE_TIPO_LABELS[t]}
              </button>
            ))}
          </div>
          <div className="flex justify-end pt-4">
            <Button plain onClick={resetAndClose}>Cancelar</Button>
          </div>
        </DialogBody>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onClose={submitting ? () => {} : resetAndClose} size="xl">
      <DialogTitle>
        <span className="text-azul-surelia">
          {isEdit ? 'Editar pendiente' : PENDIENTE_TIPO_LABELS[tipo]}
        </span>
      </DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          {targetLocked ? (
            presetResumen && (
              <div className="rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm text-zinc-700">
                {presetResumen}
              </div>
            )
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {permiteOrg
                  ? 'Relacionado con (opcional)'
                  : permiteConvenio ? 'Fiado o convenio' : 'Fiado'}
              </label>
              <SearchableListbox
                options={targetOptions}
                value={target}
                onChange={(v) => { setTarget(v); setContratoId(''); setFianzaId(''); }}
                placeholder={permiteOrg ? ORG_LABEL : ''}
                popupClassName="w-full"
              />
            </div>
          )}

          {!targetLocked && permiteContrato && contratoOptions.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {soloContrato ? 'Contrato' : 'Contrato (opcional)'}
                </label>
                <SearchableListbox
                  options={contratoOptions}
                  value={contratoId}
                  onChange={(v) => { setContratoId(v); setFianzaId(''); }}
                  placeholder={soloContrato ? '' : 'General'}
                  popupClassName="w-full"
                />
              </div>
              {permiteFianza && fianzaOptions.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Fianza (opcional)
                  </label>
                  <SearchableListbox
                    options={fianzaOptions}
                    value={fianzaId}
                    onChange={setFianzaId}
                    placeholder="General"
                    popupClassName="w-full"
                  />
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Qué falta</label>
            {sugerencias.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {sugerencias.map((s) => (
                  <button
                    key={s}
                    onClick={() => setTitulo(s)}
                    className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                      titulo === s
                        ? 'border-azul-surelia bg-azul-surelia text-white'
                        : 'border-gray-300 text-gray-700 hover:border-azul-surelia/50'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <input
              type="text"
              value={titulo}
              maxLength={TITULO_MAX_LEN}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder={sugerencias.length ? 'O escríbelo' : 'Ej. Acta de entrega de la obra'}
              className={field}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha límite (opcional)
              </label>
              <input
                type="date"
                value={fechaLimite}
                onChange={(e) => setFechaLimite(e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Asignados
              </label>
              <div className="pt-1">
                <AsignadosBubbles
                  uids={asignados}
                  miembros={miembros}
                  meUid={user?.uid}
                  onChange={setAsignados}
                />
              </div>
            </div>
            {!isEdit && preset?.otId && Array.isArray(preset?.equipos) && preset.equipos.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Para el equipo (opcional)
                </label>
                <select
                  value={equipoId}
                  onChange={(e) => setEquipoId(e.target.value)}
                  className={field}
                >
                  <option value="">Sin equipo</option>
                  {preset.equipos.map((eq) => (
                    <option key={eq.id} value={eq.id}>{eq.nombre}</option>
                  ))}
                </select>
              </div>
            )}
            {defaults?.pideAfianzadora && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Afianzadora{requiereAfianzadora(tipo) ? '' : ' (opcional)'}
                </label>
                <SearchableListbox
                  options={afianzadoraOptions}
                  value={afianzadora}
                  onChange={setAfianzadora}
                  placeholder="Selecciona la afianzadora"
                  popupClassName="w-full"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Detalle (opcional)
            </label>
            <textarea
              rows={2}
              value={detalle}
              maxLength={DETALLE_MAX_LEN}
              onChange={(e) => setDetalle(e.target.value)}
              className={field}
            />
          </div>


          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-between gap-2 pt-1">
            {!isEdit && !preset?.tipo && cards.length > 1 ? (
              <Button plain onClick={() => setTipo(null)} disabled={submitting}>
                Cambiar tipo
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button plain onClick={resetAndClose} disabled={submitting}>Cancelar</Button>
              <Button color="surelia" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Guardando…' : isEdit ? 'Guardar' : 'Registrar'}
              </Button>
            </div>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

// The raw emisor from an XML is a legal name; canonicalize it for the picker,
// but never invent one when there is nothing to canonicalize.
function mapAfianzadoraNameSafe(raw, catalogo) {
  return raw ? mapAfianzadoraName(raw, catalogo) : '';
}

export default PendienteDialog;
